import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import authgear from "../authgear";
import Header from "../components/Header";

const base_url = process.env.NODE_ENV === "production"
    ? "https://hooks-28xd.onrender.com"
    : "http://localhost:4000";

const ACCENT = "#00d4ff";
const PAGE_SIZE = 15;

function formatMoney(amount) {
    if (amount == null) return "—";
    return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function timeAgo(dateStr) {
    if (!dateStr) return "never";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

const card = {
    background: "#1a1a2e",
    border: "1px solid #2a2a4a",
    borderRadius: "10px",
    padding: "18px 20px",
};

const CategoryTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div style={{ background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "6px", padding: "8px 12px", fontSize: "12px" }}>
            <div style={{ color: "#ddd", fontWeight: "bold" }}>{p.category}</div>
            <div style={{ color: ACCENT }}>{formatMoney(p.total)}</div>
            <div style={{ color: "#666" }}>{p.count} transaction{p.count === 1 ? "" : "s"}</div>
        </div>
    );
};

const Finance = () => {
    const navigate = useNavigate();

    const [syncJob, setSyncJob] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [resyncing, setResyncing] = useState(false);
    const [resyncProgress, setResyncProgress] = useState(null);
    const [page, setPage] = useState(1);
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [asking, setAsking] = useState(false);

    const getToken = async () => {
        await authgear.refreshAccessTokenIfNeeded();
        return authgear.accessToken;
    };

    const loadAll = useCallback(async () => {
        setError(null);
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };

            const [statusRes, accountsRes, categoriesRes, transactionsRes] = await Promise.all([
                axios.get(`${base_url}/finance/sync/status`, { headers }),
                axios.get(`${base_url}/finance/accounts`, { headers }),
                axios.get(`${base_url}/finance/spend-by-category`, { headers }),
                axios.get(`${base_url}/finance/transactions?limit=200`, { headers }),
            ]);

            setSyncJob(statusRes.data.job);
            setAccounts(accountsRes.data.accounts);
            setCategories(categoriesRes.data.categories.map(c => ({ category: c._id || "Uncategorized", total: c.total, count: c.count })));
            setTransactions(transactionsRes.data.transactions);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (authgear.sessionState !== "AUTHENTICATED") {
            navigate("/login");
            return;
        }
        loadAll();
    }, [navigate, loadAll]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const token = await getToken();
            await axios.post(`${base_url}/finance/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
            await loadAll();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setSyncing(false);
        }
    };

    const handleResync = async () => {
        setResyncing(true);
        setError(null);
        setResyncProgress({ batch: 0, totalSynced: 0, done: false, offset: 0, totalCount: null });
        let batch = 0;
        let totalSynced = 0;
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };

            let resyncInProgress = true;
            while (resyncInProgress) {
                batch += 1;
                const { data } = await axios.post(`${base_url}/finance/sync`, { full: true }, { headers });
                if (!data.success) throw new Error(data.message);
                totalSynced += data.transactionsCreated + data.transactionsUpdated;
                resyncInProgress = data.resyncInProgress;
                setResyncProgress({
                    batch,
                    totalSynced,
                    done: !resyncInProgress,
                    offset: data.resyncOffset,
                    totalCount: data.resyncTotalCount,
                });
            }
            await loadAll();
            setTimeout(() => setResyncProgress(null), 4000);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            setResyncProgress(null);
        } finally {
            setResyncing(false);
        }
    };

    const handleAsk = async (e) => {
        e.preventDefault();
        if (!question.trim()) return;
        setAsking(true);
        setAnswer("");
        try {
            const token = await getToken();
            const { data } = await axios.post(`${base_url}/finance/ask`, { question }, { headers: { Authorization: `Bearer ${token}` } });
            setAnswer(data.success ? data.answer : `Error: ${data.message}`);
        } catch (err) {
            setAnswer(`Error: ${err.response?.data?.message || err.message}`);
        } finally {
            setAsking(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
    const pageTransactions = transactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const netWorth = accounts.reduce((sum, a) => sum + (a.includeInNetWorth ? a.currentBalance || 0 : 0), 0);
    const topCategories = [...categories].sort((a, b) => b.total - a.total).slice(0, 8);

    return (
        <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#ddd", fontFamily: "Verdana, sans-serif" }}>
            <Header />
            <div style={{ padding: "60px 20px 40px", maxWidth: "900px", margin: "0 auto" }}>

                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "8px" }}>
                    <h2 style={{ color: ACCENT, margin: 0 }}>Finance</h2>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {syncJob && (
                            <span style={{ fontSize: "11px", color: syncJob.status === "failed" ? "#f87171" : "#444" }}>
                                {syncJob.status === "failed" ? `Sync failed: ${syncJob.error}` : `Synced ${timeAgo(syncJob.lastRunAt)}`}
                            </span>
                        )}
                        <button
                            onClick={handleResync}
                            disabled={syncing || resyncing}
                            style={{
                                padding: "4px 12px",
                                background: "transparent",
                                border: "1px solid #2a2a4a",
                                borderRadius: "6px",
                                color: resyncing ? "#444" : "#888",
                                cursor: resyncing ? "not-allowed" : "pointer",
                                fontSize: "12px",
                            }}
                        >
                            {resyncing ? "Resyncing..." : "Resync All History"}
                        </button>
                        <button
                            onClick={handleSync}
                            disabled={syncing || resyncing}
                            style={{
                                padding: "4px 12px",
                                background: "transparent",
                                border: "1px solid #2a2a4a",
                                borderRadius: "6px",
                                color: syncing ? "#444" : "#888",
                                cursor: syncing ? "not-allowed" : "pointer",
                                fontSize: "12px",
                            }}
                        >
                            {syncing ? "Syncing..." : "Sync Now"}
                        </button>
                    </div>
                </div>

                {resyncProgress && (() => {
                    const percent = resyncProgress.totalCount
                        ? Math.min(100, Math.round((resyncProgress.offset / resyncProgress.totalCount) * 100))
                        : null;
                    const label = resyncProgress.done
                        ? `Resync complete — ${resyncProgress.totalSynced} transactions synced`
                        : resyncProgress.batch === 0
                        ? "Starting resync..."
                        : `Resyncing full history — batch ${resyncProgress.batch}, ${resyncProgress.totalSynced} transactions synced`;
                    return (
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: resyncProgress.done ? "#4ecdc4" : "#888", marginBottom: "8px" }}>
                                <span>{label}</span>
                                {percent != null && <span>{percent}%</span>}
                            </div>
                            <div style={{ height: "6px", background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "4px", overflow: "hidden" }}>
                                <div
                                    style={{
                                        height: "100%",
                                        width: resyncProgress.done ? "100%" : percent != null ? `${percent}%` : "100%",
                                        background: resyncProgress.done ? "#4ecdc4" : ACCENT,
                                        transition: "width 0.3s ease",
                                        animation: !resyncProgress.done && percent == null ? "financePulse 1.4s ease-in-out infinite" : undefined,
                                    }}
                                />
                            </div>
                            <style>{"@keyframes financePulse { 0%,100% { opacity: 0.3 } 50% { opacity: 1 } }"}</style>
                        </div>
                    );
                })()}

                {loading ? (
                    <p style={{ color: "#555", textAlign: "center", marginTop: "60px" }}>Loading...</p>
                ) : error ? (
                    <div style={{ background: "#2a1a1a", border: "1px solid #4a2a2a", borderRadius: "8px", padding: "16px", color: "#f88", fontSize: "13px" }}>
                        Error: {error}
                    </div>
                ) : (
                    <>
                        {/* Accounts overview */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "14px" }}>
                                <h3 style={{ margin: 0, fontSize: "14px", color: "#aaa" }}>Accounts</h3>
                                <span style={{ fontSize: "18px", fontWeight: "bold", color: ACCENT }}>{formatMoney(netWorth)}</span>
                            </div>
                            {accounts.length === 0 ? (
                                <p style={{ color: "#555", fontSize: "13px" }}>No accounts synced yet. Click Sync Now.</p>
                            ) : (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px" }}>
                                    {accounts.map(a => (
                                        <div key={a.id} style={{ background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "8px", padding: "12px 14px" }}>
                                            <div style={{ fontSize: "12px", color: "#ddd", fontWeight: "bold" }}>{a.displayName}</div>
                                            <div style={{ fontSize: "10px", color: "#555", marginTop: "2px" }}>{a.institutionName} · {a.type}</div>
                                            <div style={{ fontSize: "15px", color: a.currentBalance < 0 ? "#f87171" : "#ddd", marginTop: "6px" }}>
                                                {formatMoney(a.currentBalance)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Spend by category */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Spend by category</h3>
                            {topCategories.length === 0 ? (
                                <p style={{ color: "#555", fontSize: "13px" }}>No transactions synced yet.</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={Math.max(180, topCategories.length * 34)}>
                                    <BarChart data={topCategories} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }} barCategoryGap={10}>
                                        <XAxis type="number" hide />
                                        <YAxis
                                            type="category"
                                            dataKey="category"
                                            width={110}
                                            tick={{ fill: "#888", fontSize: 11 }}
                                            axisLine={{ stroke: "#2a2a4a" }}
                                            tickLine={false}
                                        />
                                        <Tooltip content={<CategoryTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                                        <Bar dataKey="total" radius={[0, 4, 4, 0]} maxBarSize={18}>
                                            {topCategories.map((c, i) => <Cell key={i} fill={ACCENT} />)}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        {/* Ask panel */}
                        <div style={{ ...card, marginBottom: "20px" }}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Ask about your finances</h3>
                            <form onSubmit={handleAsk} style={{ display: "flex", gap: "8px" }}>
                                <input
                                    value={question}
                                    onChange={e => setQuestion(e.target.value)}
                                    placeholder="How much did I spend on dining last month?"
                                    style={{
                                        flex: 1,
                                        padding: "8px 12px",
                                        background: "#0a0a14",
                                        border: "1px solid #2a2a4a",
                                        borderRadius: "6px",
                                        color: "#ddd",
                                        fontSize: "13px",
                                    }}
                                />
                                <button
                                    type="submit"
                                    disabled={asking}
                                    style={{
                                        padding: "8px 16px",
                                        background: asking ? "#0a1a2a" : "#0a2a3a",
                                        border: "1px solid #2a3a4a",
                                        borderRadius: "6px",
                                        color: asking ? "#444" : ACCENT,
                                        cursor: asking ? "not-allowed" : "pointer",
                                        fontSize: "13px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {asking ? "Thinking..." : "Ask"}
                                </button>
                            </form>
                            {answer && (
                                <div style={{ marginTop: "12px", padding: "12px 14px", background: "#0a0a14", border: "1px solid #2a2a4a", borderRadius: "8px", fontSize: "13px", lineHeight: "1.5" }}>
                                    {answer}
                                </div>
                            )}
                        </div>

                        {/* Transactions table */}
                        <div style={card}>
                            <h3 style={{ margin: "0 0 14px", fontSize: "14px", color: "#aaa" }}>Transactions</h3>
                            {transactions.length === 0 ? (
                                <p style={{ color: "#555", fontSize: "13px" }}>No transactions synced yet.</p>
                            ) : (
                                <>
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                                            <thead>
                                                <tr style={{ borderBottom: "1px solid #2a2a4a", color: "#666", textAlign: "left" }}>
                                                    <th style={{ padding: "6px 8px" }}>Date</th>
                                                    <th style={{ padding: "6px 8px" }}>Merchant</th>
                                                    <th style={{ padding: "6px 8px" }}>Category</th>
                                                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pageTransactions.map(t => (
                                                    <tr key={t._id} style={{ borderBottom: "1px solid #1a1a2e" }}>
                                                        <td style={{ padding: "6px 8px", color: "#888", whiteSpace: "nowrap" }}>{formatDate(t.date)}</td>
                                                        <td style={{ padding: "6px 8px", color: "#ddd" }}>{t.merchantName}</td>
                                                        <td style={{ padding: "6px 8px", color: "#888" }}>{t.category || "—"}</td>
                                                        <td style={{ padding: "6px 8px", textAlign: "right", color: t.isCashOut ? "#f87171" : "#4ecdc4", fontVariantNumeric: "tabular-nums" }}>
                                                            {formatMoney(t.amount)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "14px" }}>
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            style={{ padding: "4px 10px", background: "transparent", border: "1px solid #2a2a4a", borderRadius: "6px", color: page === 1 ? "#444" : "#888", cursor: page === 1 ? "not-allowed" : "pointer", fontSize: "12px" }}
                                        >
                                            Prev
                                        </button>
                                        <span style={{ fontSize: "11px", color: "#555" }}>Page {page} of {totalPages}</span>
                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page === totalPages}
                                            style={{ padding: "4px 10px", background: "transparent", border: "1px solid #2a2a4a", borderRadius: "6px", color: page === totalPages ? "#444" : "#888", cursor: page === totalPages ? "not-allowed" : "pointer", fontSize: "12px" }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Finance;
